/**
 * Exact decimal arithmetic for display math on the decimal strings the API sends
 * ("0.15", "12.345", "100"). Values are a bigint coefficient and a scale, so nothing ever
 * passes through binary floating point. Stored values are computed by the backend engines;
 * use this only to derive what the screen shows (a percentage from a fraction, a line total,
 * a preview of a rescaled amount).
 *
 *   Decimal.from("0.15").shift(2).toString()          // "15.00" -> "15"  (fraction to percent)
 *   Decimal.from("12.5").times("0.2").toFixed(3)      // "2.500"
 *   sumDecimals(["0.1", "0.2"]).toString()            // "0.3"
 */

/** A decimal number serialized as a string, as the Django Ninja API renders it. */
export type DecimalString = string;
export type DecimalInput = Decimal | DecimalString | number | bigint;

/** Same names and semantics as `Intl.NumberFormat`'s `roundingMode`. */
export type RoundingMode = "halfExpand" | "halfEven" | "trunc" | "floor" | "ceil" | "expand";

const DECIMAL_PATTERN = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent);
}

function absBigInt(value: bigint): bigint {
  return value < 0n ? -value : value;
}

/** Divides with the given rounding mode; `divisor` must be positive. */
function divideRounded(dividend: bigint, divisor: bigint, mode: RoundingMode): bigint {
  const quotient = dividend / divisor; // truncates toward zero
  const remainder = dividend % divisor;
  if (remainder === 0n) return quotient;
  const negative = dividend < 0n;
  const twiceRemainder = absBigInt(remainder) * 2n;
  const awayFromZero = negative ? quotient - 1n : quotient + 1n;
  switch (mode) {
    case "trunc":
      return quotient;
    case "expand":
      return awayFromZero;
    case "floor":
      return negative ? quotient - 1n : quotient;
    case "ceil":
      return negative ? quotient : quotient + 1n;
    case "halfExpand":
      return twiceRemainder >= divisor ? awayFromZero : quotient;
    case "halfEven":
      if (twiceRemainder > divisor) return awayFromZero;
      if (twiceRemainder < divisor) return quotient;
      return quotient % 2n === 0n ? quotient : awayFromZero;
  }
}

export class Decimal {
  /** The value is `coefficient / 10^scale`. */
  readonly coefficient: bigint;
  readonly scale: number;

  private constructor(coefficient: bigint, scale: number) {
    this.coefficient = coefficient;
    this.scale = scale;
  }

  static readonly ZERO = new Decimal(0n, 0);
  static readonly ONE = new Decimal(1n, 0);

  /** Parses a decimal string, number or bigint. Throws a RangeError for anything else. */
  static from(value: DecimalInput): Decimal {
    const parsed = Decimal.tryFrom(value);
    if (parsed === null) throw new RangeError(`Not a decimal number: ${String(value)}`);
    return parsed;
  }

  /** Like `from`, but returns null for null, undefined, "" and unparseable input. */
  static tryFrom(value: DecimalInput | null | undefined): Decimal | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Decimal) return value;
    if (typeof value === "bigint") return new Decimal(value, 0);
    if (typeof value === "number") {
      if (!Number.isFinite(value)) return null;
      return Decimal.tryFrom(String(value));
    }
    const match = DECIMAL_PATTERN.exec(value.trim());
    if (!match) return null;
    const [, sign, integerDigits = "", fractionDigits = "", exponentText] = match;
    if (integerDigits === "" && fractionDigits === "") return null;
    let coefficient = BigInt(`${integerDigits}${fractionDigits}` || "0");
    let scale = fractionDigits.length - Number(exponentText ?? 0);
    if (scale < 0) {
      coefficient *= powerOfTen(-scale);
      scale = 0;
    }
    return new Decimal(sign === "-" ? -coefficient : coefficient, scale);
  }

  private static aligned(a: Decimal, b: Decimal): [bigint, bigint, number] {
    const scale = Math.max(a.scale, b.scale);
    return [a.coefficient * powerOfTen(scale - a.scale), b.coefficient * powerOfTen(scale - b.scale), scale];
  }

  plus(other: DecimalInput): Decimal {
    const [a, b, scale] = Decimal.aligned(this, Decimal.from(other));
    return new Decimal(a + b, scale);
  }

  minus(other: DecimalInput): Decimal {
    const [a, b, scale] = Decimal.aligned(this, Decimal.from(other));
    return new Decimal(a - b, scale);
  }

  times(other: DecimalInput): Decimal {
    const factor = Decimal.from(other);
    return new Decimal(this.coefficient * factor.coefficient, this.scale + factor.scale);
  }

  /** Division rounded to `scale` decimal places. Throws a RangeError when dividing by zero. */
  dividedBy(other: DecimalInput, scale = 12, mode: RoundingMode = "halfEven"): Decimal {
    const divisor = Decimal.from(other);
    if (divisor.coefficient === 0n) throw new RangeError("Division by zero");
    // this / divisor = (c1 / 10^s1) / (c2 / 10^s2); scaled to `scale` places.
    let numerator = this.coefficient * powerOfTen(scale + divisor.scale);
    let denominator = divisor.coefficient * powerOfTen(this.scale);
    if (denominator < 0n) {
      numerator = -numerator;
      denominator = -denominator;
    }
    return new Decimal(divideRounded(numerator, denominator, mode), scale);
  }

  /** Multiplies by 10^places without rounding: fraction to percent is `shift(2)`. */
  shift(places: number): Decimal {
    if (places >= 0) {
      if (places <= this.scale) return new Decimal(this.coefficient, this.scale - places);
      return new Decimal(this.coefficient * powerOfTen(places - this.scale), 0);
    }
    return new Decimal(this.coefficient, this.scale - places);
  }

  /** Rounds to `scale` decimal places (never adds places; see `toFixed` for padding). */
  round(scale: number, mode: RoundingMode = "halfExpand"): Decimal {
    if (scale >= this.scale) return this;
    return new Decimal(divideRounded(this.coefficient, powerOfTen(this.scale - scale), mode), scale);
  }

  /** Drops trailing zeros after the decimal point: 12.300 becomes 12.3. */
  normalize(): Decimal {
    let { coefficient, scale } = this;
    while (scale > 0 && coefficient % 10n === 0n) {
      coefficient /= 10n;
      scale -= 1;
    }
    return new Decimal(coefficient, scale);
  }

  compare(other: DecimalInput): -1 | 0 | 1 {
    const [a, b] = Decimal.aligned(this, Decimal.from(other));
    return a === b ? 0 : a < b ? -1 : 1;
  }

  equals(other: DecimalInput): boolean {
    return this.compare(other) === 0;
  }

  isZero(): boolean {
    return this.coefficient === 0n;
  }

  isNegative(): boolean {
    return this.coefficient < 0n;
  }

  abs(): Decimal {
    return this.isNegative() ? this.negate() : this;
  }

  negate(): Decimal {
    return new Decimal(-this.coefficient, this.scale);
  }

  /** Plain notation that keeps the current scale: "12.300", "-0.5", "100". */
  toString(): DecimalString {
    const digits = absBigInt(this.coefficient)
      .toString()
      .padStart(this.scale + 1, "0");
    const sign = this.coefficient < 0n ? "-" : "";
    if (this.scale === 0) return `${sign}${digits}`;
    const point = digits.length - this.scale;
    return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
  }

  /** Exactly `scale` decimal places, rounded with `mode`: Decimal.from("1.5").toFixed(3) is "1.500". */
  toFixed(scale: number, mode: RoundingMode = "halfExpand"): DecimalString {
    const rounded = this.round(scale, mode);
    return new Decimal(rounded.coefficient * powerOfTen(scale - rounded.scale), scale).toString();
  }

  /** For chart geometry and other non-stored values only. */
  toNumber(): number {
    return Number(this.toString());
  }

  toJSON(): DecimalString {
    return this.toString();
  }
}

/** Sum of decimal strings; empty input is zero. */
export function sumDecimals(values: Iterable<DecimalInput>): Decimal {
  let total = Decimal.ZERO;
  for (const value of values) total = total.plus(value);
  return total;
}

/** "0.15" -> "15" (exact; trailing zeros dropped). */
export function fractionToPercent(fraction: DecimalInput): DecimalString {
  return Decimal.from(fraction).shift(2).normalize().toString();
}

/** "15" -> "0.15" (exact; trailing zeros dropped). */
export function percentToFraction(percent: DecimalInput): DecimalString {
  return Decimal.from(percent).shift(-2).normalize().toString();
}

/** True for strings the API could send as a decimal ("12", "-0.5", "1E-7"). */
export function isDecimalString(value: unknown): value is DecimalString {
  return typeof value === "string" && Decimal.tryFrom(value) !== null;
}

/**
 * Converts a number from an input control to a decimal string without float artefacts:
 * numberToDecimalString(0.1 + 0.2, 3) is "0.3".
 */
export function numberToDecimalString(value: number, maxDecimals = 8): DecimalString {
  if (!Number.isFinite(value)) throw new RangeError(`Not a finite number: ${value}`);
  return Decimal.from(value.toFixed(Math.min(Math.max(maxDecimals, 0), 100)))
    .normalize()
    .toString();
}
